"use client";

import { useSession } from "next-auth/react";
import { useMemo } from "react";
import { decodeJwt } from "jose";

export type ImpersonationInfo = {
  /** True when impersonatingEmail is present in the JWT — user is an employee using Switch Supplier */
  isEmployee: boolean;
  impersonatingEmail: string | null;
  accountSfId: string | null;
  /**
   * The email to record as the creator of an action:
   * - impersonatingEmail when an employee is impersonating
   * - session.user.email for a normal supplier
   */
  effectiveUserEmail: string | null;
};

export const useImpersonation = (): ImpersonationInfo => {
  const { data: session } = useSession();

  return useMemo<ImpersonationInfo>(() => {
    const sessionEmail = session?.user?.email ?? null;

    const empty: ImpersonationInfo = {
      isEmployee: false,
      impersonatingEmail: null,
      accountSfId: null,
      effectiveUserEmail: sessionEmail,
    };

    if (!session?.user?.access_token) return empty;

    try {
      const claims = decodeJwt(session.user.access_token);

      if (claims.impersonatingEmail && typeof claims.impersonatingEmail === "string") {
        const email = claims.impersonatingEmail;
        const sfId =
          claims.accountSfId && typeof claims.accountSfId === "string" ? claims.accountSfId : null;

        return {
          isEmployee: true,
          impersonatingEmail: email,
          accountSfId: sfId,
          effectiveUserEmail: email,
        };
      }

      return empty;
    } catch (err) {
      console.warn("Error decoding JWT token:", err);
      return empty;
    }
  }, [session]);
};
